import React from 'react';
import { Service } from '../../../shared/hooks/useServiceSelection';
import ServiceCard from './ServiceCard';
import ServiceList from './ServiceList';

interface ServiceDisplayProps {
  services: Service[];
  selectedServices: Service[];
  onToggle: (service: Service) => void;
  formatDuration: (minutes: number) => string;
  currency: { symbol: string };
  shouldShowReadMore: (text: string) => boolean;
  onReadMore: (id: string) => void;
  nodeData: any;
}

const ServiceDisplay: React.FC<ServiceDisplayProps> = ({
  services,
  selectedServices,
  onToggle,
  formatDuration,
  currency,
  shouldShowReadMore,
  onReadMore,
  nodeData
}) => {
  // Extract settings from nodeData or use defaults
  const settings = nodeData?.settings || {
    displayMode: 'cards',
    showPrices: true,
    showImages: true,
    showDuration: true,
    showDescriptions: true,
    displayStyle: 'card',
    itemsPerRow: 3,
    numberRows: 2,
    colors: {}
  };



  // Extract colors from settings
  const colors = settings?.colors || {};

  // Function to calculate grid columns based on itemsPerRow
  const getGridClass = (itemsPerRow: number) => {
    switch (itemsPerRow) {
      case 1:
        return 'lp-grid-cols-1';
      case 2:
        return 'lp-grid-cols-1 md:lp-grid-cols-2';
      case 3:
        return 'lp-grid-cols-1 md:lp-grid-cols-2 lg:lp-grid-cols-3';
      case 4:
        return 'lp-grid-cols-1 md:lp-grid-cols-2 lg:lp-grid-cols-4';
      default:
        return 'lp-grid-cols-1 md:lp-grid-cols-2';
    }
  };

  // Determine which component to render based on displayStyle/displayMode
  if (settings.displayMode === 'list' || settings.displayStyle === 'list') {
    return (
      <ServiceList
        services={services}
        selectedServices={selectedServices}
        onToggle={onToggle}
        formatDuration={formatDuration}
        currency={currency}
        colors={colors}
        showPrices={settings.showPrices}
        showDuration={settings.showDuration}
      />
    );
  } else {
    // For 'cards' mode or any other mode
    return (
      <div className={`lp-grid lp-gap-4 ${getGridClass(settings.itemsPerRow || 2)}`}>
        {services.map(service => (
          <ServiceCard
            key={service.id}
            service={service}
            isSelected={selectedServices.some(s => s.id === service.id)}
            onToggle={onToggle}
            formatDuration={formatDuration}
            currency={currency}
            shouldShowReadMore={shouldShowReadMore}
            onReadMore={onReadMore}
            colors={colors}
            showPrices={settings.showPrices}
            showDuration={settings.showDuration}
            showDescriptions={settings.showDescriptions}
            showImages={settings.showImages}
          />
        ))}
      </div>
    );
  }
};

export default ServiceDisplay;
